import { useEffect, useMemo, useState } from 'react';
import { fetchAllProducts } from '../../api/products';
import { refreshProductsFromRemote } from '../../api/refreshProductsFromRemote';
import { updateProductLocal, deleteProductImage } from '../../api/adminProducts';
import SearchBar from '../../components/SearchBar';
import AdminProductsHeader from '../../components/admin/AdminProductsHeader';
import ProductsGrid from '../../components/admin/ProductsGrid';
import ProductsTable from '../../components/admin/ProductsTable';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [view, setView] = useState('cards'); // 'cards' | 'table'
  const [q, setQ] = useState('');
  const [inStock, setInStock] = useState(true);
  const [activeProducts, setActiveProducts] = useState(true);
  // ephemeral per-row UI state for image uploads & quick edit
  const [rowUI, setRowUI] = useState({}); // { [productId]: { files: File[], urlsText: string, mode: 'keep'|'append'|'replace', open: boolean, pendingFiles?: File[] } }

  // single source of truth loader
  const loadProducts = async () => {
    const data = await fetchAllProducts();
    // support both array and { products: [] } response shapes
    const list = Array.isArray(data) ? data : data?.products || [];
    setProducts(list);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadProducts();
      } catch (e) {
        console.error('Load products failed', e);
      }
    })();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // 1) sync DB with remote
      await refreshProductsFromRemote(); // { products, added, updated, ... } but we ignore the payload

      // 2) re-fetch from the *same* endpoint we use everywhere (includes images)
      await loadProducts();

      setLastRefreshedAt(new Date());
    } catch (e) {
      console.error('Refresh failed', e);
      alert(e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleLocalChange = (id, field, value) => {
    setProducts(prev =>
      prev.map(p => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const setRowUiField = (id, field, value) => {
    setRowUI(prev => ({
      ...prev,
      [id]: {
        files: [],
        urlsText: '',
        open: prev[id]?.open ?? false,
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  const toggleRowOpen = (id) => {
    setRowUI(prev => ({
      ...prev,
      [id]: {
        files: [],
        urlsText: '',
        ...(prev[id] || {}),
        open: !prev[id]?.open,
      },
    }));
  };

  const parseUrls = (urlsText) =>
    (urlsText || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);

  const handleSave = async (p) => {
    setSavingId(p.id);
    const ui = rowUI[p.id] || {};
    const files = (ui.pendingFiles && ui.pendingFiles.length > 0)
      ? ui.pendingFiles
      : (ui.files || []);

    const imagesOptions = files.length > 0
      ? { files, imageUrls: [], replaceImages: false, appendImages: true }
      : undefined;

    try {
      const updated = await updateProductLocal(
        p.id,
        {
          subname: p.subname ?? '',
          description: p.description ?? '',
          sponsored: !!p.sponsored,
          is_active: !!p.is_active,
        },
        imagesOptions
      );

      // assume backend returns full product including images
      setProducts(prev => prev.map(x => (x.id === p.id ? updated : x)));

      setRowUI(prev => ({
        ...prev,
        [p.id]: { files: [], urlsText: '', open: false, pendingFiles: [] },
      }));
    } catch (e) {
      console.error('Save failed', e);
      alert(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const onDeleteExistingImage = async (productId, img) => {
    try {
      await deleteProductImage(productId, img.id);
      setProducts(prev =>
        prev.map(p =>
          p.id !== productId
            ? p
            : { ...p, images: (p.images || []).filter(i => i.id !== img.id) }
        )
      );
    } catch (e) {
      console.error('Delete image failed', e);
      alert('Failed to delete image: ' + (e?.message || 'unknown error'));
    }
  };

  const onStockChange = (event) => {
    setInStock(event.target.checked);
  };

  const onActiveProductsChange = (event) => {
    setActiveProducts(event.target.checked);
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = products;

    if (inStock) out = out.filter(p => Number(p?.stock_count ?? 0) > 0);
    if (activeProducts) out = out.filter(p => p.is_active);

    if (term) {
      out = out.filter(p => {
        const hay = [
          p.product_name,
          p.subname,
          p.service_name,
          p.product_group_name,
          String(p.product_id),
        ]
          .filter(Boolean)
          .join(' | ')
          .toLowerCase();

        return hay.includes(term);
      });
    }

    return out;
  }, [products, q, inStock, activeProducts]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <AdminProductsHeader
        totalCount={products.length}
        filteredCount={filtered.length}
        lastRefreshedAt={lastRefreshedAt}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        view={view}
        setView={setView}
      >
        <div>
          <SearchBar
            value={q}
            onChange={setQ}
            placeholder="Search by name, id, group…"
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="inStock"
              checked={inStock}
              onChange={onStockChange}
            />
            <label htmlFor="inStock">Prikaži proizvode koji su na stanju</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="activeProducts"
              checked={activeProducts}
              onChange={onActiveProductsChange}
            />
            <label htmlFor="activeProducts">Prikaži samo proizvode koji su aktivni</label>
          </div>
        </div>
      </AdminProductsHeader>

      {view === 'cards' ? (
        <ProductsGrid
          products={filtered}
          rowUI={rowUI}
          toggleRowOpen={toggleRowOpen}
          setRowUiField={setRowUiField}
          handleLocalChange={handleLocalChange}
          handleSave={handleSave}
          savingId={savingId}
          onDeleteExistingImage={onDeleteExistingImage}
        />
      ) : (
        <ProductsTable
          products={filtered}
          rowUI={rowUI}
          toggleRowOpen={toggleRowOpen}
          setRowUiField={setRowUiField}
          handleLocalChange={handleLocalChange}
          handleSave={handleSave}
          savingId={savingId}
          onDeleteExistingImage={onDeleteExistingImage}
        />
      )}
    </div>
  );
}
